// modeCurrencies — THE console's one statement of which currencies
// belong to which trading mode.
//
// It mirrors the server's projection (contracts vocab CurrenciesForMode,
// consumed by ledger's mode-filtered reads) and exists so no component
// hardcodes "GAME means RED and GREEN" at its own call site. If a
// fourth currency ever ships — the canonical example being an EMERALD
// that trades alongside GOLD in CASH mode — it is one line HERE plus
// the server-side projection, and every surface that groups, filters
// or labels by mode follows: this file is deliberately data, not
// switch statements.
//
// Scalability rules encoded by the shape:
//   - a MODE maps to a LIST (never a single currency): CASH carrying
//     [GOLD, EMERALD] one day is a data change, not a refactor;
//   - the spend/payout distinction is per-mode data too, because a
//     mode's debit and credit currencies already differ (GAME debits
//     RED, credits GREEN) and a second CASH currency would need the
//     same expressiveness.
//
// The registry (GET /admin/currencies) stays the authority on each
// currency's own config; this module only says which mode(s) it
// trades in.

export type TradingMode = "CASH" | "GAME";

export interface ModeCurrencyPolicy {
  /** Every currency that can appear on this mode's surfaces. */
  currencies: readonly string[];
  /** What a BUY debits. */
  spend: string;
  /** What sells, redemptions and payouts credit. */
  payout: string;
}

export const MODE_CURRENCIES: Readonly<Record<TradingMode, ModeCurrencyPolicy>> =
  Object.freeze({
    CASH: Object.freeze({ currencies: ["GOLD"] as const, spend: "GOLD", payout: "GOLD" }),
    GAME: Object.freeze({ currencies: ["RED", "GREEN"] as const, spend: "RED", payout: "GREEN" }),
  });

/** Modes a currency trades in — [] for a currency outside every mode
 *  (e.g. a promo-only currency), which a caller must render as such
 *  rather than defaulting it into CASH. */
export function modesForCurrency(code: string): TradingMode[] {
  return (Object.keys(MODE_CURRENCIES) as TradingMode[]).filter((m) =>
    MODE_CURRENCIES[m].currencies.includes(code),
  );
}
